import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

class CartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-id'];

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#load('complementary');
  }

  attributeChangedCallback(name, previousValue, currentValue) {
    if (name !== 'data-product-id' || !previousValue || previousValue === currentValue || !this.isConnected) return;

    const list = this.querySelector('[data-recommendations-list]');
    if (list) list.replaceChildren();
    this.querySelector('.cart-drawer__recommendations-title')?.replaceChildren(document.createTextNode('Complétez votre achat'));
    this.#load('complementary');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = async (event) => {
    const button = event.target.closest('[data-add-recommendation]');
    if (!button) return;

    event.preventDefault();
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: 'cart-drawer-section',
          sections_url: window.location.pathname,
        }),
      });
      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Unable to add recommendation');
      }

      if (payload.sections?.['cart-drawer-section']) {
        await morphSection('cart-drawer-section', payload.sections['cart-drawer-section'], {
          mode: 'hydration',
        });
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-recommendations',
          didError: false,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Unable to add recommendation',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      button.disabled = false;
    }
  };

  async #load(intent) {
    const productId = this.dataset.productId;
    const list = this.querySelector('[data-recommendations-list]');
    if (!productId || !list) return;

    try {
      const response = await fetch(
        `${this.dataset.url}?product_id=${productId}&limit=${this.dataset.limit || 4}&intent=${intent}`
      );
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

      const payload = await response.json();
      const cartProductIds = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
      const products = (payload.products || []).filter((recommendedProduct) => {
        return !cartProductIds.has(String(recommendedProduct.id));
      });

      if (products.length === 0) {
        this.#removeRecommendationRow();
        return;
      }

      list.innerHTML = products.map((recommendedProduct) => this.#renderProduct(recommendedProduct)).join('');
      this.hidden = false;
    } catch (error) {
      console.warn('[cart-recommendations]', error);
      this.#removeRecommendationRow();
    }
  }

  #removeRecommendationRow() {
    this.closest('.cart-items__recommendations-row')?.remove();
    if (this.isConnected) this.remove();
  }

  #renderProduct(product) {
    const image = product.featured_image || product.images?.[0];
    const imageMarkup = image
      ? `<img class="cart-drawer__recommendation-image" src="${image}" alt="" loading="lazy">`
      : '';

    const variantId = product.variants?.[0]?.id;
    const productUrl = this.#escape(product.url || `/products/${product.handle}`);
    const addButton = variantId
      ? `<button class="button button-secondary cart-drawer__recommendation-add" type="button" data-add-recommendation data-variant-id="${variantId}">Ajouter</button>`
      : '';

    return `<div class="cart-drawer__recommendation" data-product-id="${product.id}">
      <a href="${productUrl}">
      ${imageMarkup}
      <span class="cart-drawer__recommendation-title">${this.#escape(product.title)}</span>
      </a>
      ${addButton}
    </div>`;
  }

  #escape(value) {
    const element = document.createElement('textarea');
    element.textContent = value || '';
    return element.innerHTML;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
